import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
  Spinner,
  useToast,
} from "../../../design-system";
import { createAbsence } from "../../../helpers/kronaly_helper";
import { ABSENCE_TYPES } from "./absenceHelpers";

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

/**
 * Modal de "Añadir ausencia".
 *
 * Props:
 *   - open: boolean
 *   - employeeProfileId: id del empleado al que se le crea la ausencia.
 *     Cuando es manager creando para otro empleado, se pasa explícitamente.
 *     Cuando es el propio empleado (vista Ausencias), pasamos su propio profile id.
 *   - onClose, onCreated
 */
const NewAbsenceModal = ({ open, employeeProfileId, onClose, onCreated }) => {
  const toast = useToast();
  const [type, setType] = useState("vacation");
  const [startDate, setStartDate] = useState(todayYmd());
  const [endDate, setEndDate] = useState(todayYmd());
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setType("vacation");
    setStartDate(todayYmd());
    setEndDate(todayYmd());
    setReason("");
    setError(null);
  }, [open]);

  const days = useMemo(() => {
    if (!startDate || !endDate) return null;
    const a = new Date(`${startDate}T00:00:00`);
    const b = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return null;
    return Math.round((b - a) / 86400000) + 1;
  }, [startDate, endDate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!employeeProfileId) {
      setError("No se ha podido identificar el empleado.");
      return;
    }
    if (!startDate || !endDate || days === null) {
      setError("Revisa las fechas.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const r = await createAbsence({
        employee_profile_id: employeeProfileId,
        type,
        start_date: startDate,
        end_date: endDate,
        reason: reason.trim() || null,
      });
      const created = unwrap(r);
      toast.success("Ausencia solicitada", `${days} día${days > 1 ? "s" : ""}.`);
      if (onCreated) onCreated(created);
      onClose();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "No se pudo crear la ausencia.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Añadir ausencia" size="md">
      <form onSubmit={onSubmit}>
        <ModalBody>
          {error && (
            <div style={{ marginBottom: 12 }}>
              <Alert tone="danger">{error}</Alert>
            </div>
          )}

          <Field label="Tipo" required>
            <Select value={type} onChange={(e) => setType(e.target.value)}>
              {ABSENCE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </Select>
          </Field>

          <div className="kr-grid kr-gap-3 kr-mt-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <Field label="Inicio" required>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
              />
            </Field>
            <Field label="Fin" required>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                min={startDate || undefined}
              />
            </Field>
          </div>

          {days !== null && (
            <div
              style={{
                marginTop: 8,
                padding: "8px 12px",
                background: "var(--kr-bg-subtle)",
                borderRadius: 6,
                fontSize: 12,
                color: "var(--kr-fg-muted)",
              }}
            >
              Duración estimada: <strong style={{ color: "var(--kr-fg-strong)" }}>{days} día{days > 1 ? "s" : ""}</strong> (días naturales).
            </div>
          )}

          <div className="kr-mt-3">
            <Field label="Motivo (opcional)">
              <Input
                type="textarea"
                rows={3}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={2000}
                placeholder="Ej. Vacaciones de verano"
              />
            </Field>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" type="button" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={saving || days === null}>
            {saving && <Spinner size={12} color="#fff" style={{ marginRight: 6 }} />}
            {saving ? "Solicitando…" : "Solicitar"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default NewAbsenceModal;
